import React, { useEffect, useRef, useState } from 'react';

import * as NavigationBar from 'expo-navigation-bar';
import { SplashScreen as ExpoSplashScreen } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import * as StatusBar from 'expo-status-bar';
import { VideoView, useVideoPlayer } from 'expo-video';
import LottieView from 'lottie-react-native';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';

import { maybePlayAudioOnce } from 'components/app-takeover/play-audio-once';
import { getAppTakeover } from 'components/app-takeover/use-app-takeover';
import { isAndroid } from 'utils/environment';
import {
  animatedSplashScreenHiddenDeferred,
  nativeSplashScreenHiddenDeferred,
} from 'utils/hide-splash-screen';
import logger from 'utils/logger';

import { AppInner } from './AppInner';
import { AnimatedSplashScreen } from './components/splash-screen/splash-screen';

if (isAndroid) {
  // normalize android to work like iOS as much as possible to minimize visual inconsistencies
  (async () => {
    try {
      await NavigationBar.setBackgroundColorAsync('transparent');
      await NavigationBar.setBehaviorAsync('overlay-swipe');
    } catch {}
  })();
  StatusBar.setStatusBarTranslucent(true);
  StatusBar.setStatusBarBackgroundColor('transparent');
  SplashScreen.setOptions({
    fade: false,
  });
}

const bkBounceSplashJson = require('./assets/splash-screen.json');

export const LOTTIE_SPLASH_SCREEN_DURATION_MS = 1800;
export const WIGGLE_ROOM = 800;

export const App: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [animatedSplashVisible, setAnimatedSplashVisible] = useState(
    !AnimatedSplashScreen.getHideSplashPreInit()
  );

  const {
    lottie: appTakeoverLottie,
    audio: appTakeoverAudio,
    video: appTakeoverVideo,
    durationMs: appTakeoverDurationMs,
  } = getAppTakeover()?.splashScreen || {};

  const splashScreenDuration =
    (appTakeoverDurationMs ?? LOTTIE_SPLASH_SCREEN_DURATION_MS) + WIGGLE_ROOM;

  useEffect(() => {
    if (appTakeoverAudio) {
      maybePlayAudioOnce(appTakeoverAudio);
    }

    if (Platform.OS === 'web') {
      // no splashscreen on web but we need to resolve these promises which other components depend on.
      nativeSplashScreenHiddenDeferred.resolve();
      animatedSplashScreenHiddenDeferred.resolve();
      return;
    }

    // required for deep linking situations
    ExpoSplashScreen.hideAsync().finally(() => nativeSplashScreenHiddenDeferred.resolve());

    // FAILSAFE - make sure all splash screens hide if something goes bad
    setTimeout(() => {
      setAnimatedSplashVisible(oldVal => {
        if (oldVal) {
          logger.warn({
            message: `Hiding splash screen took longer than ${splashScreenDuration}ms. This SHOULD NOT happen and should be investigated but it has been forced to work for this session.`,
          });
          animatedSplashScreenHiddenDeferred.resolve();
          return false;
        }
        return oldVal;
      });
    }, splashScreenDuration);
  }, []);

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <AppInner routes={children} />
      {animatedSplashVisible &&
        (appTakeoverVideo ? (
          <SplashVideo
            video={appTakeoverVideo}
            onComplete={() => {
              setAnimatedSplashVisible(false);
              animatedSplashScreenHiddenDeferred.resolve();
            }}
          />
        ) : (
          <LottieView
            resizeMode="cover"
            hardwareAccelerationAndroid
            autoPlay
            source={appTakeoverLottie || bkBounceSplashJson}
            style={styles.lottieView}
            loop={false}
            onAnimationFinish={() => {
              setAnimatedSplashVisible(false);
              animatedSplashScreenHiddenDeferred.resolve();
            }}
            onAnimationFailure={err => {
              setAnimatedSplashVisible(false);
              animatedSplashScreenHiddenDeferred.resolve();
            }}
          />
        ))}
    </SafeAreaProvider>
  );
};

// separate component to avoid any native video stuff unless its active due to API design
const SplashVideo = ({ video, onComplete }: { video: any; onComplete: () => void }) => {
  const hasCompletedRef = useRef(false);

  const markComplete = () => {
    if (hasCompletedRef.current) {
      return;
    }
    hasCompletedRef.current = true;
    onComplete();
  };

  const player = useVideoPlayer(video, player => {
    player.loop = false;
    player.muted = true;
    player.audioMixingMode = 'mixWithOthers';
    player.addListener('playToEnd', markComplete);
    player.addListener('statusChange', payload => {
      if (payload.error) {
        markComplete();
        return;
      }

      if (payload.status === 'idle' && payload.oldStatus === 'readyToPlay') {
        markComplete();
      }
    });
    player.play();
  });
  return (
    <VideoView
      style={styles.splashVideo}
      contentFit="cover"
      nativeControls={false}
      allowsPictureInPicture={false}
      // @ts-ignore - VideoPlayer type mismatch between native and web
      player={player}
    />
  );
};

const styles = StyleSheet.create({
  lottieView: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  splashVideo: {
    ...StyleSheet.absoluteFillObject,
  },
});
